import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { FileUpload } from '@/components/ui/file-upload';
import { Image, Building2 } from 'lucide-react';
import { EditableCardActions } from './EditableCardActions';
import { uploadSchoolFile } from '@/api/schools';
import type { SchoolEditData } from '@/types/school';

interface BrandingImagesCardProps {
  school: SchoolEditData;
  onSaved: () => void;
}

export function BrandingImagesCard({ school, onSaved }: BrandingImagesCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const hasWhiteLabel = !!school.whiteLabelEntityId;

  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleFileUpload = async (
    field: 'logo' | 'logoSignin' | 'favicon' | 'cover',
    file: File
  ) => {
    try {
      setUploadingField(field);
      await uploadSchoolFile(school.code, field, file);
      onSaved();
    } catch (err) {
      console.error('Error uploading file:', err);
    } finally {
      setUploadingField(null);
    }
  };

  const allImages = [
    { label: 'Logo principal', url: school.logoUrl, key: 'logo' as const },
    ...(hasWhiteLabel
      ? [
          { label: 'Logo connexion', url: school.logoSigninUrl, key: 'logoSignin' as const },
          { label: 'Favicon', url: school.faviconUrl, key: 'favicon' as const },
          { label: 'Couverture', url: school.coverUrl, key: 'cover' as const },
        ]
      : []),
  ];

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Image />}
          iconColor="pink"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={() => setIsEditing(false)}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Images</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`grid gap-4 ${hasWhiteLabel ? 'grid-cols-2' : 'grid-cols-1 max-w-xs'}`}>
            {allImages.map(({ label, url }) => (
              <div key={label} className="space-y-2">
                <Label className="text-sm">{label}</Label>
                {url ? (
                  <div className="border bg-muted overflow-hidden flex items-center justify-center h-[200px]">
                    <img
                      src={url}
                      alt={label}
                      className="max-h-[180px] w-auto max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="border bg-muted/50 flex items-center justify-center h-[200px]">
                    <Building2 className="h-8 w-8 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {!hasWhiteLabel && (
            <p className="text-xs text-muted-foreground mt-4">
              Les images supplémentaires (logo connexion, favicon, couverture) sont disponibles lorsqu'une marque blanche est configurée.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<Image />}
        iconColor="pink"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={false}
            onEdit={startEditing}
            onSave={() => setIsEditing(false)}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Images</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`grid gap-4 ${hasWhiteLabel ? 'grid-cols-2' : 'grid-cols-1 max-w-xs'}`}>
          <div className="space-y-2">
            <Label>Logo principal</Label>
            <FileUpload
              previewUrl={school.logoUrl}
              onChange={() => {}}
              onUpload={(file) => handleFileUpload('logo', file)}
              uploading={uploadingField === 'logo'}
              label="Logo"
              description="Affiché sur la plateforme"
            />
          </div>

          {hasWhiteLabel && (
            <>
              <div className="space-y-2">
                <Label>Logo connexion</Label>
                <FileUpload
                  previewUrl={school.logoSigninUrl}
                  onChange={() => {}}
                  onUpload={(file) => handleFileUpload('logoSignin', file)}
                  uploading={uploadingField === 'logoSignin'}
                  label="Logo connexion"
                  description="Page de connexion"
                  maxSize={1 * 1024 * 1024}
                />
              </div>

              <div className="space-y-2">
                <Label>Favicon</Label>
                <FileUpload
                  previewUrl={school.faviconUrl}
                  onChange={() => {}}
                  onUpload={(file) => handleFileUpload('favicon', file)}
                  uploading={uploadingField === 'favicon'}
                  label="Favicon"
                  description="Icône navigateur"
                />
              </div>

              <div className="space-y-2">
                <Label>Image de couverture</Label>
                <FileUpload
                  previewUrl={school.coverUrl}
                  onChange={() => {}}
                  onUpload={(file) => handleFileUpload('cover', file)}
                  uploading={uploadingField === 'cover'}
                  label="Couverture"
                  description="Page de connexion"
                />
              </div>
            </>
          )}
        </div>
        {!hasWhiteLabel && (
          <p className="text-xs text-muted-foreground mt-4">
            Les images supplémentaires (logo connexion, favicon, couverture) sont disponibles lorsqu'une marque blanche est configurée.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
